/**
 * Navigation handlers (nav:*).
 */

import type { FlowStep } from "../types/flow";
import { resolveRecord, resolveValue } from "../expressions/resolve";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

/**
 * Resolve the target path/value for a navigation step.
 *
 * The `to` field may be a literal string, a binding expression, or a
 * reference to a state path. This helper normalises it to a string.
 */
function resolveTarget(step: FlowStep, ctx: unknown): string {
 const r = asRecord(step);
 const toValue = resolveValue(r["to"], ctx as any);
 return typeof toValue === "string" ? toValue.trim() : "";
}

/**
 * Determine whether a target should be treated as a *route* navigation.
 *
 * Absolute pathname strings (e.g. "/chat/abc") are intended to navigate
 * within the same host. When passed to `nav:screen`, the previous
 * implementation stripped the host, resulting in URLs like
 * "https://chat/abc". Detecting a leading slash lets us forward the
 * navigation as a route, preserving the host.
 */
function isAbsolutePath(target: string): boolean {
 return target.startsWith("/");
}

registerStepHandler("nav:screen", (step, ctx) => {
 const target = resolveTarget(step, ctx);
 if (!target) return;

 const params = resolveRecord(asRecord(step)["params"] as Record<string, unknown>, ctx);
 const replace = Boolean(resolveValue(asRecord(step)["replace"], ctx));

 // If the target looks like an absolute path, treat it as a route navigation
 // to keep the host part intact.
 if (isAbsolutePath(target)) {
 ctx.navigation.navigate({ kind: "route", to: target, params, replace });
 } else {
 ctx.navigation.navigate({ kind: "screen", to: target, params, replace });
 }
});

registerStepHandler("nav:route", (step, ctx) => {
 const r = asRecord(step);
 const toValue = resolveValue(r["to"], ctx);
 const to = typeof toValue === "string" ? toValue.trim() : "";
 if (!to) return;
 const params = resolveRecord(r["params"] as Record<string, unknown>, ctx);
 const replace = Boolean(resolveValue(r["replace"], ctx));
 ctx.navigation.navigate({ kind: "route", to, params, replace });
});